// One photo, full size, with a Download link -- and the shared pages' answer to
// a gesture that used to leave the app.
//
// WHAT IT REPLACES. Both shared logbook pages did
// `window.location.href = photo.url`, which navigates the SPA away to a raw S3
// URL: Back re-runs the whole share fetch, and an hour later the presign has
// expired so the destination is a dead XML error page. There was no full-size
// view anywhere on the route and no way to save anything.
//
// IT RENDERS THROUGH AttachmentImage, not a bare <img>, and that is the point
// rather than an economy. An expired presigned URL is the ordinary failure on
// this page -- links are minted per page load with a one-hour ceiling and people
// bookmark the page -- and that component already owns the whole story: a
// reserved box so the layout doesn't jump, the skeleton shimmer, a spinner with
// the byte count while a multi-megabyte original downloads, and an inline
// "couldn't load" glyph when it doesn't arrive. A bare <img> would give a broken
// -image icon and no explanation. `src` is the presigned URL, which makes
// AttachmentImage skip its storage lookup (an anonymous visitor has no Amplify
// Storage); `imageKey` is passed too, and is only used in the failure message.
//
// PREV/NEXT is here because a viewer that can only show the photo you clicked is
// the thing everyone immediately tries to swipe past. It costs one index prop.
//
// A download failure is reported INSIDE the dialog, never through the host page's
// status: one unreadable photo must not look like a page that failed to load
// (.claude/rules/logbook.md). The fallback offered is "Open in a new tab", which
// at least shows the photo when saving it is what broke.
//
// REMOVE is optional and defaults to absent, which is every existing caller and
// the behaviour this had before the prop existed. It is offered only by the
// shared TASK list, and only for a photo a visitor added: the endpoint refuses
// anything else by name (`isGuestPhotoName` is the whole of "a visitor never
// deletes the owner's photos"), and a control that will be refused is worse than
// no control. The guard is repeated HERE rather than trusted to the caller,
// because this component is the thing that draws the button.
//
// Props:
//   photos   - the list being browsed
//   index    - which one is open; null/undefined means closed
//   onIndex  - step to another index (prev/next)
//   onHide   - dismissed
//   onRemove - optional; `(photo) => Promise`. Shown only for a guest-named photo.

import { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AttachmentImage, formatBytes } from './AttachmentImage';
import { downloadPhoto, downloadNameFor } from '../utilities/photoDownloads.mjs';
import { isGuestPhotoName } from '../fleet-shared/Attachments.mjs';

export function PhotoViewer({ photos, index, onIndex, onHide, onRemove }) {
  const list = photos ?? [];
  const open = Number.isInteger(index) && index >= 0 && index < list.length;
  const photo = open ? list[index] : null;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A new photo is a new question: a failure to save the last one says nothing
  // about this one, and leaving the message up would be simply wrong.
  useEffect(() => {
    setError(null);
  }, [photo?.key]);

  if (!photo) return null;

  const name = downloadNameFor(photo);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadPhoto(photo);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // Only a visitor's own upload, and only when a caller offered the capability
  // at all. See the header.
  const canRemove = Boolean(onRemove) && isGuestPhotoName(photo.name);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await onRemove(photo);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
    // No `finally`: a successful remove closes the dialog from the caller's
    // side, so clearing `busy` here would be a state update on an unmounted
    // component.
  };

  return (
    <Modal show onHide={onHide} size="lg" fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="text-truncate">{name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* The sizing class goes on a wrapper: .tp-attachment-thumb takes its
            width from its parent, and its inner `position: absolute; inset: 0`
            rule is load-bearing in WebKit and must not be overridden. */}
        <div className="tp-photo-viewer-image">
          <AttachmentImage
            imageKey={photo.key}
            src={photo.url}
            alt={name}
            sizeBytes={photo.size}
          />
        </div>

        {error && (
          <div className="alert alert-warning py-2 small mt-3 mb-0" role="alert">
            {error.message}{' '}
            <a href={photo.url} target="_blank" rel="noopener noreferrer">
              Open in a new tab
            </a>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div className="text-body-secondary small">
          {photo.size ? formatBytes(photo.size) : null}
          {isGuestPhotoName(photo.name) && (
            <span className="ms-2">Added by a visitor</span>
          )}
          {list.length > 1 && (
            <span className="ms-2">{index + 1} of {list.length}</span>
          )}
        </div>

        <div className="d-flex gap-2">
          {canRemove && (
            <Button variant="outline-danger" onClick={remove} disabled={busy}>
              <i className="fa-regular fa-trash-can me-2" aria-hidden="true"></i>
              Remove
            </Button>
          )}
          {list.length > 1 && (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => onIndex(index - 1)}
                disabled={index === 0}
                aria-label="Previous photo"
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => onIndex(index + 1)}
                disabled={index === list.length - 1}
                aria-label="Next photo"
              >
                <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </Button>
            </>
          )}
          <Button variant="primary" onClick={save} disabled={busy}>
            {busy ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" aria-hidden="true"></i>
                Downloading...
              </>
            ) : (
              <>
                <i className="fa-solid fa-download me-2" aria-hidden="true"></i>
                Download
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default PhotoViewer;
